import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

export default function SettingsTab({ user, centerData }) {
  const [notifs, setNotifs] = useState({ newJob: true, jobUpdate: true, payment: true, review: true });
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd]         = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdMsg, setPwdMsg]         = useState('');
  const [savingPwd, setSavingPwd]   = useState(false);
  const [savingNotifs, setSavingNotifs] = useState(false);
  const [notifMsg, setNotifMsg]     = useState('');

  useEffect(() => {
    if (centerData?.notifications) setNotifs(centerData.notifications);
  }, [centerData]);

  const saveNotifs = async () => {
    setSavingNotifs(true);
    try {
      await updateDoc(doc(db, 'serviceCenters', user.uid), { notifications: notifs, updatedAt: serverTimestamp() });
      setNotifMsg('Saved!');
      setTimeout(() => setNotifMsg(''), 2000);
    } catch (e) { setNotifMsg('Error: ' + e.message); }
    finally { setSavingNotifs(false); }
  };

  const changePassword = async () => {
    if (!currentPwd || !newPwd || !confirmPwd) { setPwdMsg('All fields are required.'); return; }
    if (newPwd !== confirmPwd) { setPwdMsg('New passwords do not match.'); return; }
    if (newPwd.length < 6) { setPwdMsg('Password must be at least 6 characters.'); return; }
    setSavingPwd(true); setPwdMsg('');
    try {
      const cred = EmailAuthProvider.credential(user.email, currentPwd);
      await reauthenticateWithCredential(auth.currentUser, cred);
      await updatePassword(auth.currentUser, newPwd);
      setPwdMsg('Password updated successfully!');
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (e) {
      setPwdMsg(e.code === 'auth/wrong-password' ? 'Current password is incorrect.' : e.message);
    } finally { setSavingPwd(false); }
  };

  const iS = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, color: '#111827', background: '#f9fafb', outline: 'none', boxSizing: 'border-box', marginBottom: 12 };
  const lS = { display: 'block', fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };

  const NotifRow = ({ id, label, sub }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{label}</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{sub}</div>
      </div>
      <div onClick={() => setNotifs(n => ({ ...n, [id]: !n[id] }))} style={{ width: 44, height: 24, borderRadius: 12, background: notifs[id] ? '#F97316' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
        <div style={{ position: 'absolute', top: 2, left: notifs[id] ? 22 : 2, width: 20, height: 20, borderRadius: 10, background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
      </div>
    </div>
  );

  return (
    <div style={{ padding: 32, maxWidth: 600 }}>
      <h1 style={{ fontSize: 24, fontWeight: 900, marginBottom: 28 }}>Settings</h1>

      {/* Account Info */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, marginBottom: 16 }}>Account Information</h3>
        <div style={{ display: 'flex', align: 'center', gap: 16, padding: 16, background: '#f9fafb', borderRadius: 12 }}>
          <div style={{ width: 48, height: 48, borderRadius: 24, background: '#F97316', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18, fontWeight: 900 }}>
            {(centerData?.businessName || 'B').charAt(0)}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{centerData?.businessName}</div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>{user.email}</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              <span style={{ background: '#f0fdf4', color: '#16a34a', padding: '2px 8px', borderRadius: 20, fontWeight: 700, fontSize: 11 }}>
                {centerData?.trialActive ? 'Free Trial Active' : 'Active'}
              </span>
              {centerData?.trialEndsAt && (
                <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280' }}>
                  Trial ends {new Date(centerData.trialEndsAt?.seconds * 1000).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 800 }}>Email Notifications</h3>
          <button onClick={saveNotifs} disabled={savingNotifs} style={{ background: '#F97316', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
            {savingNotifs ? 'Saving...' : notifMsg || 'Save'}
          </button>
        </div>
        <NotifRow id="newJob"    label="New Job Assigned"     sub="When CocoWash sends a new job to your center" />
        <NotifRow id="jobUpdate" label="Job Status Updates"   sub="When a driver is on the way or has arrived" />
        <NotifRow id="payment"   label="Payment Notifications" sub="When a payment is processed or statement is ready" />
        <NotifRow id="review"    label="New Customer Reviews"  sub="When a customer leaves a review for your center" />
      </div>

      {/* Change Password */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, marginBottom: 16 }}>Change Password</h3>
        {pwdMsg && <div style={{ background: pwdMsg.includes('success') ? '#f0fdf4' : '#fef2f2', color: pwdMsg.includes('success') ? '#16a34a' : '#dc2626', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{pwdMsg}</div>}
        <label style={lS}>Current Password</label>
        <input style={iS} type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} placeholder="••••••••" />
        <label style={lS}>New Password</label>
        <input style={iS} type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="At least 6 characters" />
        <label style={lS}>Confirm New Password</label>
        <input style={{ ...iS, marginBottom: 16 }} type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} placeholder="••••••••" />
        <button onClick={changePassword} disabled={savingPwd} style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
          {savingPwd ? 'Updating...' : 'Update Password'}
        </button>
      </div>

      {/* Revenue Share Info */}
      <div style={{ background: '#fff7ed', borderRadius: 16, padding: 24, border: '1px solid #fed7aa', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, marginBottom: 12 }}>Your Plan</h3>
        <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.8 }}>
          <div>Status: <strong>{centerData?.trialActive ? '90-Day Free Trial' : 'Active Partner'}</strong></div>
          <div>Revenue Share: <strong>{centerData?.cocoWashFeePercentage || 15}% on CocoWash-referred jobs after trial</strong></div>
          <div>Walk-in customers: <strong>100% yours — always</strong></div>
          {centerData?.trialEndsAt && (
            <div>Trial ends: <strong>{new Date(centerData.trialEndsAt?.seconds * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong></div>
          )}
        </div>
        <div style={{ marginTop: 12, padding: 10, background: '#fff', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
          Questions about billing? Contact us at partners@cocowash.org
        </div>
      </div>
    </div>
  );
}